(ns nl.mediquest.questionnaire-frontend.component.report.regeldruk
  (:require
   [nl.mediquest.questionnaire-frontend.component.report.regeldruk.calculations
    :refer [add-calculations]]
   [nl.mediquest.questionnaire-frontend.util :refer [get-current-year]]))

(defn percentage
  "Returns double with value as percentage of max-value. For example
  40.0 in case of 2 and 5"
  [value max-value]
  (* (/ value max-value) 100.0))

(defn header []
  [:div.h-16.border-b.border-gray-200.bg-white.z-10
   [:div.container.mx-auto.h-16.leading-none
    [:div.h-full.items-center.flex.justify-between.content-center
     [:div.flex.pl-4
      [:img.h-8 {:src "/images/mq-logo.png"}]
      [:div.border-l.border-gray-200.md:mx-10.md:block.hidden]
      [:div.text-2xl.text-primary.leading-tight.md:block.hidden.font-semibold "Uw resultaat"]]
     [:div.flex-wrap.flex.pr-4.items-center.print-hidden]]]])

(defn explanation []
  [:div.text-gray-800.w-full
   [:strong.text-xl "Uw resultaat"]
   [:p
    "Hierbij ziet u de scores per thema, zodat u een indicatie heeft van de
     relatieve scores per thema."
    [:br]
    "Een groene balk geeft een positief oordeel, een oranje balk een gemiddeld
     oordeel en een rode balk een negatief oordeel."]])

(defn footer []
  [:div.min-h-16.py-5.bg-footer.text-white.flex-shrink-0
   [:div.container.mx-auto
    [:div.h-full.flex-wrap.flex.justify-between.content-center.text-center
     [:div.w-full.md:w-auto
      [:img.inline-block.h-8.ml-4 {:src "/images/mq-logo.png"}]]
     [:div.text-center.w-full.md:w-auto.pt-1
      [:a.block.md:inline-block.text-white.no-underline
       {:href "https://home.mediquest.nl/privacy-statement/"
        :target :_blank}
       "Privacy Statement"]
      [:div.block.md:inline-block.px-4 "© " (get-current-year) " Mediquest B.V. All rights reserved."]]]]])

(def themes
  {:a {:title "Thema A" :description "Mijn mening over de kwaliteit van indicatoren"}
   :b {:title "Thema B" :description "Mijn beleefde lasten van transparantie"}
   :c {:title "Thema C" :description "Mijn beleefde opbrengsten van transparantie"}
   :d {:title "Thema D" :description "Mijn oordeel over de regeldruk van transparantie"}
   :e {:title "Thema E" :description "Mijn mening over PROMS"}
   :f {:title "Thema F" :description "Mijn ervaring met gestapelde uitvraag door 3e partijen"}
   :g {:title "Thema G" :description "Mijn nabeschouwing"}})

(defn barchart [data question-type theme-key]
  [:div.barchart
   (if-let [{:keys [value max-value]} (get-in data [:calculations question-type theme-key])]
     (let [percentage (percentage value max-value)
           red "#E5001C"
           yellow "#FFF30A"
           green "#23A63B"]
       [:div.barchart__fill
        {:style {:width (str percentage "%")
                 :background (condp > percentage
                               33 red
                               66 yellow
                               green)}}])
     "De waarde kon niet worden berekend...")])

(defn medisch-specialist? [{:keys [question_a2 question_a2kiezen] :as _data}]
  (let [medisch-specialist "1"]
    (or (= question_a2 medisch-specialist)
        (= question_a2kiezen medisch-specialist))))

(defn verpleegkundige? [{:keys [question_a2 question_a2kiezen] :as _data}]
  (let [verpleegkundige-values #{"2" "3"}]
    (or (contains? verpleegkundige-values question_a2)
        (contains? verpleegkundige-values question_a2kiezen))))

(defn kwaliteitsmedewerker? [{:keys [question_a2 question_a2kiezen] :as _data}]
  (let [kwaliteitsmedewerker "4"]
    (or (= question_a2 kwaliteitsmedewerker)
        (= question_a2kiezen kwaliteitsmedewerker))))

(defn barchart-card
  [data question-type theme-key]
  (let [{:keys [title description]} (get themes theme-key)]
    [:div.p-1.rounded-xl.group.sm:flex.space-x-6.bg-white.bg-opacity-50.shadow-xl.hover:rounded-2xl.m-4
     [:img.h-56.sm:h-full.w-full.object-cover.object-top.rounded-lg.transition.duration-500.group-hover:rounded-xl
      {:class ["sm:w-5/12"]
       :height "667"
       :width "1000"
       :loading "lazy"
       :alt theme-key
       :src (str "/custom/regeldruk/images/themes/" "a" #_(name theme-key) ".png")}]
     [:div.pl-0.p-5
      {:class ["sm:w-7/12"]}
      [:div.space-y-2
       [:div.space-y-4
        [:h4.text-2xl.font-semibold.text-cyan-900 title]
        [:p.text-gray-600 description]
        [barchart data question-type theme-key]]]]]))

(defn resultaten-medisch-specialist [data]
  [:<>
   [:div.flex-wrap.justify-center.sm:justify-start
    [barchart-card data :medisch-specialist :a "Thema A" "Mijn mening over de kwaliteit van indicatoren"]
    [barchart-card data :medisch-specialist :b "Thema B" "Mijn beleefde lasten van transparantie"]
    [barchart-card data :medisch-specialist :c "Thema C" "Mijn beleefde opbrengsten van transparantie"]
    [barchart-card data :medisch-specialist :d "Thema D" "Mijn oordeel over de regeldruk van transparantie"]]
   [:div.flex-wrap.justify-center.sm:justify-start
    [barchart-card data :medisch-specialist :e "Thema E" "Mijn mening over PROMS"]
    [barchart-card data :medisch-specialist :f "Thema F" "Mijn ervaring met gestapelde uitvraag door 3e partijen"]
    [barchart-card data :medisch-specialist :g "Thema G" "Mijn nabeschouwing"]]])

(defn resultaten-kwaliteitsmedewerker [data]
  [:<>
   [:div.flex-wrap.justify-center.sm:justify-start
    [barchart-card data :kwaliteitsmedewerker :a "Thema A" "Mijn mening over de kwaliteit van indicatoren"]
    [barchart-card data :kwaliteitsmedewerker :b "Thema B" "Mijn beleefde lasten van transparantie"]
    [barchart-card data :kwaliteitsmedewerker :c "Thema C" "Mijn beleefde opbrengsten van transparantie"]]
   [:div.flex-wrap.justify-center.sm:justify-start
    [barchart-card data :kwaliteitsmedewerker :d "Thema D" "Mijn oordeel over de regeldruk van transparantie"]
    [barchart-card data :kwaliteitsmedewerker :f "Thema F" "Mijn ervaring met gestapelde uitvraag door 3e partijen"]
    [barchart-card data :kwaliteitsmedewerker :g "Thema G" "Mijn nabeschouwing"]]])

(defn resultaten-verpleegkundige [data]
  [:<>
   [:div.d-flex.flex-wrap.justify-center.sm:justify-start
    [barchart-card data :verpleegkundige :b "Thema B" "Mijn beleefde lasten van transparantie"]
    [barchart-card data :verpleegkundige :c "Thema C" "Mijn beleefde opbrengsten van transparantie"]]
   [:div.d-flex.flex-wrap.justify-center.sm:justify-start
    [barchart-card data :verpleegkundige :d "Thema D" "Mijn oordeel over de regeldruk van transparantie"]
    [barchart-card data :verpleegkundige :g "Thema G" "Mijn nabeschouwing"]]])

(defn resultaten-box [data]
  [:div.container.m-auto.px-6.text-gray-600.md:px-12.xl:px-6
   [:div.grid.gap-12.lg:grid-cols-2
    (cond (medisch-specialist? data)
          [resultaten-medisch-specialist data]
          (verpleegkundige? data)
          [resultaten-verpleegkundige data]
          (kwaliteitsmedewerker? data)
          [resultaten-kwaliteitsmedewerker data])]])

(defn body [data]
  [:div.flex-grow.relative
   [:div.container.mx-auto.py-8
    [:div.md:flex
     [:div.w-full
      [:div.flex.flex-wrap.justify-center.sm:justify-start
       [explanation]
       [resultaten-box (add-calculations data)]]]]]])

(defn container [& components]
  (into [:div.flex.flex-col.min-h-screen.bg-gray-100] components))

(defn panel [data]
  [container
   [header]
   [body data]
   [footer]])
